import env from '../config/env';

/**
 * Currency formatting helper functions
 */

export interface CurrencyFormatOptions {
  symbol?: string;
  code?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param options - Currency formatting options
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | string | null | undefined,
  options: CurrencyFormatOptions = {}
): string => {
  if (amount === null || amount === undefined || amount === '') {
    return '0.00';
  }

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return '0.00';
  }

  const symbol = options.symbol || env.currencySymbol;
  const code = options.code || env.currencyCode;
  const minimumFractionDigits = options.minimumFractionDigits ?? 2;
  const maximumFractionDigits = options.maximumFractionDigits ?? 2;

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(numAmount);
  } catch (error) {
    // Fallback formatting if Intl.NumberFormat fails
    return `${symbol} ${numAmount.toFixed(maximumFractionDigits)}`;
  }
};

/**
 * Format a number as currency without symbol (just number)
 * @param amount - The amount to format
 * @param decimals - Number of decimal places
 * @returns Formatted number string
 */
export const formatAmount = (
  amount: number | string | null | undefined,
  decimals: number = 2
): string => {
  if (amount === null || amount === undefined || amount === '') {
    return '0.00';
  }

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return '0.00';
  }

  return numAmount.toFixed(decimals);
};

/**
 * Parse currency string to number
 * @param currencyString - Currency string to parse
 * @returns Parsed number
 */
export const parseCurrency = (currencyString: string): number => {
  if (!currencyString) return 0;
  
  // Remove currency symbols and spaces, keep only numbers and decimal point
  const cleaned = currencyString.replace(/[^\d.-]/g, '');
  const parsed = parseFloat(cleaned);
  
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Date and time formatting helper functions
 */

export interface DateFormatOptions {
  locale?: string;
  timeZone?: string;
  includeTime?: boolean;
  includeSeconds?: boolean;
  format?: 'short' | 'medium' | 'long' | 'full';
}

/**
 * Format a date string or Date object
 * @param date - Date string or Date object
 * @param options - Date formatting options
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date | null | undefined,
  options: DateFormatOptions = {}
): string => {
  if (!date) {
    return '-';
  }

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '-';
    }

    const locale = options.locale || env.dateLocale;
    const timeZone = options.timeZone || env.timeZone;
    const includeTime = options.includeTime ?? false;
    const includeSeconds = options.includeSeconds ?? false;
    const format = options.format || (includeTime ? 'medium' : 'short');

    const dateOptions: Intl.DateTimeFormatOptions = {
      timeZone,
    };

    if (includeTime) {
      dateOptions.hour = '2-digit';
      dateOptions.minute = '2-digit';
      if (includeSeconds) {
        dateOptions.second = '2-digit';
      }
    }

    switch (format) {
      case 'short':
        dateOptions.year = 'numeric';
        dateOptions.month = 'numeric';
        dateOptions.day = 'numeric';
        break;
      case 'medium':
        dateOptions.year = 'numeric';
        dateOptions.month = 'short';
        dateOptions.day = 'numeric';
        break;
      case 'long':
        dateOptions.year = 'numeric';
        dateOptions.month = 'long';
        dateOptions.day = 'numeric';
        break;
      case 'full':
        dateOptions.weekday = 'long';
        dateOptions.year = 'numeric';
        dateOptions.month = 'long';
        dateOptions.day = 'numeric';
        break;
    }

    return new Intl.DateTimeFormat(locale, dateOptions).format(dateObj);
  } catch (error) {
    // Fallback formatting
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString();
  }
};

/**
 * Format date only (without time)
 * @param date - Date string or Date object
 * @param format - Date format style
 * @returns Formatted date string
 */
export const formatDateOnly = (
  date: string | Date | null | undefined,
  format: 'short' | 'medium' | 'long' | 'full' = 'medium'
): string => {
  return formatDate(date, { format, includeTime: false });
};

/**
 * Format time only
 * @param date - Date string or Date object
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted time string
 */
export const formatTime = (
  date: string | Date | null | undefined,
  includeSeconds: boolean = false
): string => {
  if (!date) {
    return '-';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '-';
  }

  try {
    const locale = env.dateLocale;
    const timeZone = env.timeZone;

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    };

    if (includeSeconds) {
      timeOptions.second = '2-digit';
    }

    return new Intl.DateTimeFormat(locale, timeOptions).format(dateObj);
  } catch (error) {
    // Fallback formatting
    return dateObj.toLocaleTimeString();
  }
};

/**
 * Format date and time together
 * @param date - Date string or Date object
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted date and time string
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
  includeSeconds: boolean = false
): string => {
  return formatDate(date, { includeTime: true, includeSeconds, format: 'medium' });
};

/**
 * Get relative time (e.g., "2 hours ago", "3 days ago")
 * @param date - Date string or Date object
 * @returns Relative time string
 */
export const formatRelativeTime = (
  date: string | Date | null | undefined
): string => {
  if (!date) {
    return '-';
  }

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '-';
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
  } catch (error) {
    return formatDate(date);
  }
};

/**
 * Check if a date is today
 * @param date - Date string or Date object
 * @returns True if date is today
 */
export const isToday = (date: string | Date | null | undefined): boolean => {
  if (!date) return false;

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const today = new Date();
    
    return (
      dateObj.getDate() === today.getDate() &&
      dateObj.getMonth() === today.getMonth() &&
      dateObj.getFullYear() === today.getFullYear()
    );
  } catch (error) {
    return false;
  }
};

/**
 * Get environment configuration
 * @deprecated Use import env from '../config/env' instead
 */
export const getEnvConfig = () => {
  return env;
};

export const getStatusColor = (status: string | number): string => {
  if (status === undefined || status === null) return 'badge-light-danger';
  
  const statusStr = String(status);
  
  switch (statusStr) {
    case 'Pending':
    case 'Paused':
      return 'badge-light-warning';
    case 'Active':
    case 'Approved':
    case '1':
      return 'badge-light-success';
    case 'Rejected':
    case 'Blocked':
    case '2':
      return 'badge-light-danger';
    case 'Draft':
    case 'Inactive':
    case '0':
      return 'badge-light-secondary text-dark';
    default:
      return 'badge-light-secondary text-dark';
  }
};

/**
 * Make a user-entered link safe to use as an href.
 *
 * A link saved as "www.example.com" has no scheme, so the browser resolves it
 * against the current page and navigates within our own site instead of out to
 * the advertiser. Default it to https://.
 *
 * @param url - Link as the user typed it
 * @returns An absolute URL, or null if there is nothing usable to link to
 */
export const toExternalUrl = (url: string | null | undefined): string | null => {
  const trimmed = url?.trim();
  if (!trimmed) return null;

  // Already absolute (http://, https://, mailto:, tel:, ...).
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;

  // Protocol-relative ("//example.com").
  if (trimmed.startsWith('//')) return `https:${trimmed}`;

  return `https://${trimmed}`;
};

